import { mediaQueryLarge, onDocumentLoaded } from '@theme/utilities';

const productInformationGridSelector = '.product-information__grid:not(.product-information--media-none)';
const detailsSelector = '.product-details';
const stockStatusSelector = '.variant-stock-status';
const stockStatusContentSelector = '.variant-stock-status__content';
const minLockedMediaHeight = 300;

/**
 * @param {HTMLElement} grid
 */
function unlockMediaHeight(grid) {
  grid.removeAttribute('data-media-height-locked');
  grid.removeAttribute('data-media-height-capped');
  grid.style.removeProperty('--product-information-locked-media-height');
}

/**
 * Height the locked gallery may take before it outruns the screen: the
 * viewport less the header (sticky or not, so the answer holds once the
 * header pins) and the breathing room the CSS cap uses.
 *
 * @returns {number}
 */
function getViewportCap() {
  const headerHeight = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
  return window.innerHeight - headerHeight - 20;
}

/**
 * @param {HTMLElement} grid
 * @returns {number}
 */
function getDetailsHeight(grid) {
  const details = grid.querySelector(detailsSelector);
  if (!(details instanceof HTMLElement)) return 0;

  const detailsHeight = details.getBoundingClientRect().height;
  const expandedStockStatuses = details.querySelectorAll(`${stockStatusSelector}[open]`);
  let expandedStockHeight = 0;

  for (const stockStatus of expandedStockStatuses) {
    const content = stockStatus.querySelector(stockStatusContentSelector);
    if (!(content instanceof HTMLElement)) continue;

    const styles = window.getComputedStyle(content);
    const marginBlockStart = Number.parseFloat(styles.marginBlockStart) || 0;
    const marginBlockEnd = Number.parseFloat(styles.marginBlockEnd) || 0;

    expandedStockHeight += content.getBoundingClientRect().height + marginBlockStart + marginBlockEnd;
  }

  // CCC: the same courtesy for every other opened disclosure in the column
  // (Why buy, Product details, the pickup card's (i) text): opening one
  // used to grow the gallery, or flip it into the capped sticky mode.
  for (const disclosure of details.querySelectorAll('details[open]')) {
    if (disclosure.matches(stockStatusSelector)) continue;
    for (const child of disclosure.children) {
      if (child.tagName === 'SUMMARY' || !(child instanceof HTMLElement)) continue;
      const styles = window.getComputedStyle(child);
      expandedStockHeight += child.getBoundingClientRect().height + (Number.parseFloat(styles.marginBlockStart) || 0) + (Number.parseFloat(styles.marginBlockEnd) || 0);
    }
  }
  for (const info of details.querySelectorAll('[data-ccpa-info]:not([hidden])')) {
    if (!(info instanceof HTMLElement)) continue;
    const styles = window.getComputedStyle(info);
    expandedStockHeight += info.getBoundingClientRect().height + (Number.parseFloat(styles.marginBlockStart) || 0) + (Number.parseFloat(styles.marginBlockEnd) || 0);
  }

  return Math.max(Math.round(detailsHeight - expandedStockHeight), minLockedMediaHeight);
}

/**
 * Locks the media column to the details column height. Re-runs whenever the
 * details column or the window resizes (fold/unfold, tablet rotation,
 * late-loading blocks), but only writes when a value changes so the
 * stock-status expansion that getDetailsHeight already discounts never moves
 * the gallery. When the details outrun the screen (folds, portrait tablets,
 * long buy boxes) the grid is marked capped and the CSS hands the gallery back
 * to Horizon's in-flow, screen-limited, sticky layout.
 *
 * @param {HTMLElement} grid
 */
function lockMediaHeight(grid) {
  const height = getDetailsHeight(grid);
  if (height <= 0) return;

  const capped = height > getViewportCap() ? 'true' : '';
  if (grid.dataset.mediaHeightCapped !== capped) {
    if (capped) {
      grid.dataset.mediaHeightCapped = capped;
    } else {
      grid.removeAttribute('data-media-height-capped');
    }
  }

  const value = `${height}px`;
  if (
    grid.dataset.mediaHeightLocked === 'true' &&
    grid.style.getPropertyValue('--product-information-locked-media-height') === value
  ) {
    return;
  }

  grid.style.setProperty('--product-information-locked-media-height', value);
  grid.dataset.mediaHeightLocked = 'true';
}

/** @type {WeakSet<Element>} */
const observedDetails = new WeakSet();

const detailsResizeObserver =
  typeof ResizeObserver === 'function'
    ? new ResizeObserver((entries) => {
        for (const entry of entries) {
          const grid = entry.target.closest(productInformationGridSelector);
          if (!(grid instanceof HTMLElement)) continue;

          if (!mediaQueryLarge.matches) {
            unlockMediaHeight(grid);
            continue;
          }

          lockMediaHeight(grid);
        }
      })
    : null;

/**
 * @param {HTMLElement} grid
 */
function observeDetails(grid) {
  if (!detailsResizeObserver) return;

  const details = grid.querySelector(detailsSelector);
  if (!(details instanceof HTMLElement) || observedDetails.has(details)) return;

  observedDetails.add(details);
  detailsResizeObserver.observe(details);
}

/**
 * @param {ParentNode} [root]
 */
function lockProductInformationMedia(root = document) {
  const grids = root.querySelectorAll(productInformationGridSelector);

  for (const grid of grids) {
    if (!(grid instanceof HTMLElement)) continue;

    observeDetails(grid);

    if (!mediaQueryLarge.matches) {
      unlockMediaHeight(grid);
      continue;
    }

    lockMediaHeight(grid);
  }
}

let scheduledFrame = 0;

function scheduleMediaHeightLock() {
  if (scheduledFrame) return;

  scheduledFrame = requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      scheduledFrame = 0;
      lockProductInformationMedia();
    });
  });
}

/**
 * @param {Event} event
 */
function handleStockStatusToggle(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement) || !target.matches(stockStatusSelector)) return;

  const grid = target.closest(productInformationGridSelector);
  if (!(grid instanceof HTMLElement)) return;

  lockProductInformationMedia(grid.parentElement || document);
}

onDocumentLoaded(scheduleMediaHeightLock);

mediaQueryLarge.addEventListener('change', scheduleMediaHeightLock);
/* Viewport height changes (rotation, fold/unfold) move the cap without
   necessarily resizing the details column. */
window.addEventListener('resize', scheduleMediaHeightLock);
document.addEventListener('toggle', handleStockStatusToggle, true);
document.addEventListener('shopify:section:load', scheduleMediaHeightLock);
